import { useCallback, useState } from 'react';
import {
  createMoment,
  sendA2mPush,
  deleteMoment,
  dialParty,
  dismissSuggestion,
  MediatorServiceError,
  pinSuggestion,
  relabelSpeaker,
  type CopilotSuggestion,
  type PhoneJoin,
} from '../api/mediatorService';
import type { LiveFeedState } from '../hooks/useTranscript';
import { KeyMomentsPanel } from '../components/KeyMomentsPanel';
import { SuggestionsTray } from '../components/SuggestionsTray';
import { TranscriptView } from '../components/TranscriptView';
import type { Case } from '../types/case';
import type { Session } from '../api/mediatorService';

interface Props {
  case: Case;
  session: Session;
  accessToken: string;
  /** Lifted feed so it persists across Briefing ↔ Live tab switches. */
  feed: LiveFeedState;
  onEnd: () => void;
  onEndUnresolved: (reason?: string) => void;
  ending: boolean;
}

const rankSuggestion = (s: CopilotSuggestion): number => {
  const pinBonus = s.status === 'pinned' ? 1000 : 0;
  const priorityBonus = s.priority === 'high' ? 100 : s.priority === 'medium' ? 10 : 0;
  return pinBonus + priorityBonus + new Date(s.generated_at).getTime() / 1e10;
};

export function LiveSessionPage({
  case: c,
  session,
  accessToken,
  feed,
  onEnd,
  onEndUnresolved,
  ending,
}: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [addingMoment, setAddingMoment] = useState(false);
  // Suggestions tray takes over the main view area when open; transcript is
  // hidden behind it (not squeezed). Mutually exclusive.
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  // Inline confirm for ending WITHOUT agreement: the Zoom webview
  // suppresses window.confirm/prompt (button did nothing — found live,
  // 2026-08-20), so this is real UI, not a dialog.
  const [confirmUnresolved, setConfirmUnresolved] = useState(false);
  const [unresolvedReason, setUnresolvedReason] = useState('');
  // Speaker relabels applied locally to already-received lines; the DO
  // handles future lines and the engine rewrites the DB.
  const [relabels, setRelabels] = useState<Record<string, string>>({});
  const [phoneNote, setPhoneNote] = useState<string | null>(null);
  // A2M banner: parties still unsigned (server-computed at load). Pushed =
  // local per-party "link sent" state; dismissedA2m hides after verbal note.
  const [a2mPushed, setA2mPushed] = useState<Set<string>>(new Set());
  const [a2mDismissed, setA2mDismissed] = useState(false);
  const a2mMissing = (session.a2m_missing ?? []).filter(Boolean);

  const relabelOptions = [
    ...c.parties.filter((p) => ['claimant', 'respondent', 'participant', 'representative'].includes(p.role_archetype)).map((p) => p.name),
    'Mediator',
  ];
  const handleRelabel = useCallback(
    (from: string, to: string) => {
      setRelabels((prev) => ({ ...prev, [from]: to }));
      relabelSpeaker(session.id, from, to, accessToken).catch((err) => {
        setRelabels((prev) => {
          const next = { ...prev };
          delete next[from];
          return next;
        });
        setPhoneNote(`⚠ Rename failed: ${err instanceof Error ? err.message : 'try again'}`);
      });
    },
    [session.id, accessToken],
  );
  // Inline confirm — window.confirm is silently blocked inside the Zoom
  // webview (same reason ArchiveButton is inline; Danny hit it live
  // 2026-09-14: pressing Call did nothing).
  const [pendingDial, setPendingDial] = useState<string | null>(null);
  const handleDialLive = useCallback(
    async (partyId: string, name: string, phone: string) => {
      if (pendingDial !== partyId) {
        setPendingDial(partyId);
        setPhoneNote(`Call ${name} at ${phone}? Press the button again to place the call.`);
        window.setTimeout(() => {
          setPendingDial((prev) => (prev === partyId ? null : prev));
          setPhoneNote((prev) => (prev?.startsWith(`Call ${name}`) ? null : prev));
        }, 8_000);
        return;
      }
      setPendingDial(null);
      setPhoneNote(`Calling ${name}…`);
      try {
        await dialParty(session.id, partyId, accessToken);
        setPhoneNote(`📞 Zoom is calling ${phone} — they press 1 to join`);
        window.setTimeout(() => setPhoneNote(null), 15_000);
      } catch (err) {
        setPhoneNote(`⚠ ${err instanceof MediatorServiceError ? err.message : 'Call failed'}`);
      }
    },
    [session.id, accessToken, pendingDial],
  );
  const phoneJoin = (session.metadata?.phone_join as PhoneJoin | undefined) ?? null;
  const displaySegments = feed.segments.map((seg) =>
    relabels[seg.speaker_label] ? { ...seg, speaker_label: relabels[seg.speaker_label] } : seg,
  );

  const placeholder =
    feed.status === 'connecting'
      ? 'Connecting to the transcript stream…'
      : feed.status === 'syncing'
        ? 'Connected — syncing…'
        : feed.status === 'error' || feed.status === 'closed'
          ? 'Transcript connection lost — retrying…'
          : session.status !== 'in_progress'
            ? 'Waiting for audio. Start RTMS in Zoom (or run the mock-session script).'
            : 'Listening… (nothing transcribed yet — check mics are unmuted)';

  const handleDismiss = useCallback(
    async (suggestionId: string) => {
      setDismissed((s) => new Set(s).add(suggestionId));
      try {
        await dismissSuggestion(session.id, suggestionId, accessToken);
      } catch (err) {
        console.error('[live] dismiss failed', err);
      }
    },
    [session.id, accessToken],
  );

  const handlePin = useCallback(
    async (suggestionId: string) => {
      try {
        await pinSuggestion(session.id, suggestionId, accessToken);
      } catch (err) {
        console.error('[live] pin failed', err);
      }
    },
    [session.id, accessToken],
  );

  const handleAddMoment = useCallback(
    async (summary: string) => {
      setAddingMoment(true);
      try {
        await createMoment(session.id, summary, accessToken);
      } catch (err) {
        console.error('[live] add moment failed', err);
      } finally {
        setAddingMoment(false);
      }
    },
    [session.id, accessToken],
  );

  const handleDeleteMoment = useCallback(
    async (momentId: string) => {
      try {
        await deleteMoment(session.id, momentId, accessToken);
      } catch (err) {
        console.error('[live] delete moment failed', err);
      }
    },
    [session.id, accessToken],
  );

  const activeSuggestions = feed.suggestions
    .filter((s) => s.status !== 'dismissed' && !dismissed.has(s.id))
    .sort((a, b) => rankSuggestion(b) - rankSuggestion(a));

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-surface-border px-3 py-2">
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-wider text-gray-500">
            Live session
          </div>
          <div className="truncate text-sm font-semibold">
            {(c.metadata as { property_name?: string }).property_name ?? 'Mediation session'}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <button
            type="button"
            className="btn-secondary !py-1 !text-xs"
            onClick={onEnd}
            disabled={ending || session.status === 'ended'}
          >
            {ending ? 'Ending…' : session.status === 'ended' ? 'Ended' : 'End session'}
          </button>
          {session.status !== 'ended' && (
            <button
              type="button"
              className="text-[11px] text-red-700 hover:underline disabled:opacity-40"
              title="Ends the session with no agreement. The Report of Mediation drafts automatically for your review; no stipulation is created."
              disabled={ending}
              onClick={() => setConfirmUnresolved((v) => !v)}
            >
              End without agreement
            </button>
          )}
        </div>
      </header>

      {a2mMissing.length > 0 && !a2mDismissed && session.status !== 'ended' && (
        <div className="shrink-0 border-b border-amber-300 bg-amber-50 px-3 py-2" role="alert">
          <div className="text-xs font-semibold text-amber-900">
            ✍️ Agreement to Mediate not signed: {a2mMissing.map((p) => p.name).join(', ')}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {a2mMissing.map((p) => (
              <button
                key={p.party_id}
                type="button"
                className="rounded border border-amber-400 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                disabled={a2mPushed.has(p.party_id)}
                onClick={() => {
                  setA2mPushed((prev) => new Set(prev).add(p.party_id));
                  sendA2mPush(session.id, p.party_id, accessToken).catch(() => {
                    setA2mPushed((prev) => { const n = new Set(prev); n.delete(p.party_id); return n; });
                  });
                }}
              >
                {a2mPushed.has(p.party_id) ? `✓ Link sent to ${p.name}` : `Send signing link to ${p.name}`}
              </button>
            ))}
            <button type="button" className="text-[11px] text-amber-800 underline" onClick={() => setA2mDismissed(true)}
              title="If they agree verbally instead, record it on their party card in the dashboard.">
              Covered verbally — dismiss
            </button>
          </div>
        </div>
      )}

      {feed.captureDropped && session.status !== 'ended' && (
        <div className="shrink-0 border-b border-amber-300 bg-amber-100 px-3 py-2" role="alert">
          <div className="text-xs font-semibold text-amber-900">
            ⚠ Transcription stopped
          </div>
          <div className="mt-0.5 text-[11px] text-amber-800">
            We&rsquo;re no longer capturing audio. Please rejoin the Zoom meeting (or confirm cloud
            recording is on) to restore the transcript — anything said now won&rsquo;t be captured.
          </div>
        </div>
      )}

      {confirmUnresolved && session.status !== 'ended' && (
        <div className="shrink-0 border-b border-red-200 bg-red-50 p-3">
          <div className="text-xs font-medium text-red-800">
            End this mediation WITHOUT an agreement? The Report of Mediation will note that no
            resolution was reached and draft for your review. No stipulation is created.
          </div>
          <input
            type="text"
            className="mt-2 w-full rounded-md border border-red-200 px-2 py-1.5 text-sm"
            placeholder="Reason (optional) — appears on the report"
            value={unresolvedReason}
            onChange={(e) => setUnresolvedReason(e.target.value)}
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className="rounded-md bg-red-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
              disabled={ending}
              onClick={() => {
                setConfirmUnresolved(false);
                onEndUnresolved(unresolvedReason.trim() || undefined);
              }}
            >
              {ending ? 'Ending…' : 'Confirm: end without agreement'}
            </button>
            <button type="button" className="btn-secondary !py-1.5 !text-xs" onClick={() => setConfirmUnresolved(false)}>
              Keep going
            </button>
          </div>
        </div>
      )}

      <SuggestionsTray
        suggestions={activeSuggestions}
        parties={c.parties}
        onDismiss={handleDismiss}
        onPin={handlePin}
        open={suggestionsOpen}
        onToggle={setSuggestionsOpen}
      />

      {!suggestionsOpen && (
        <div className="min-h-0 flex-1">
          <TranscriptView
            segments={displaySegments}
            parties={c.parties}
            placeholder={placeholder}
            relabelOptions={relabelOptions}
            onRelabel={handleRelabel}
          />
        </div>
      )}

      {(phoneJoin || phoneNote || c.parties.some((p) => p.phone)) && (
        <div className="shrink-0 border-t border-surface-border px-3 py-1.5 text-[11px] text-gray-600">
          {phoneJoin && (
            <span className="mr-3">
              📞 Dial-in: <span className="font-mono">{phoneJoin.number}</span> · ID{' '}
              <span className="font-mono">{phoneJoin.meeting_id.replace(/(\d{3})(\d{4})(\d+)/, '$1 $2 $3')}</span>
              {phoneJoin.passcode ? <> · passcode <span className="font-mono">{phoneJoin.passcode}</span></> : null}
            </span>
          )}
          {c.parties
            .filter((p) => p.phone && ['claimant', 'respondent', 'participant', 'representative'].includes(p.role_archetype))
            .map((p) => (
              <button
                key={p.id}
                type="button"
                className={`mr-2 ${pendingDial === p.id ? 'rounded bg-brand px-1.5 font-medium text-white' : 'text-brand hover:underline'}`}
                onClick={() => void handleDialLive(p.id, p.name, p.phone!)}
              >
                📞 {pendingDial === p.id ? `Confirm call to ${p.name.split(' ')[0]}` : `Call ${p.name.split(' ')[0]}`}
              </button>
            ))}
          {phoneNote && <span className="text-gray-800">{phoneNote}</span>}
        </div>
      )}

      <div className="shrink-0 border-t border-surface-border p-2">
        <KeyMomentsPanel
          moments={feed.moments}
          parties={c.parties}
          onAdd={handleAddMoment}
          onDelete={handleDeleteMoment}
          adding={addingMoment}
        />
      </div>

      {feed.status === 'error' && (
        <div className="shrink-0 border-t border-surface-border bg-red-50 px-3 py-1.5 text-xs text-red-700">
          Transcript connection error — retrying…
        </div>
      )}
    </div>
  );
}
